import React, { useMemo, useState } from "react";

const COLOR = {
  green: {
    label: "Good",
    short: "G",
    text: "text-slate-900",
    bg: "bg-green-50",
    border: "border-green-300",
    pill: "bg-green-50 text-green-700 border-green-300",
    dot: "bg-green-500",
    mark: "bg-transparent text-slate-900",
  },
  yellow: {
    label: "Almost",
    short: "Y",
    text: "text-yellow-800",
    bg: "bg-yellow-50",
    border: "border-yellow-300",
    pill: "bg-yellow-50 text-yellow-800 border-yellow-300",
    dot: "bg-yellow-400",
    mark: "bg-yellow-100 text-yellow-900 ring-1 ring-yellow-300",
  },
  red: {
    label: "Needs work",
    short: "R",
    text: "text-red-700",
    bg: "bg-red-50",
    border: "border-red-300",
    pill: "bg-red-50 text-red-700 border-red-300",
    dot: "bg-red-500",
    mark: "bg-red-100 text-red-800 ring-1 ring-red-300",
  },
};

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function colorOf(value) {
  return COLOR[value] || COLOR.green;
}

function countChanges(reply) {
  let words = 0;
  let letters = 0;

  for (const word of reply?.ratings || []) {
    if (word.rating && word.rating !== "green") words += 1;

    for (const letter of word.letters || []) {
      if (letter.rating && letter.rating !== "green") letters += 1;
    }
  }

  const sentenceChanged =
    reply?.sentenceRating && reply.sentenceRating !== "green" ? 1 : 0;

  return {
    sentenceChanged,
    words,
    letters,
    total: sentenceChanged + words + letters,
  };
}

function getNote(reply) {
  const note = reply?.content || reply?.text || reply?.note || "";

  if (!note || note === "Human pronunciation verdict") return "";

  return String(note).trim();
}

function getChapterDisplay(reply) {
  return reply?.chapterTitle || reply?.file || "";
}

function SummaryPill({ sentenceRating, changes, chapterDisplay }) {
  const meta = colorOf(sentenceRating);

  const label =
    changes.total === 0
      ? "All green"
      : `${changes.total} mark${changes.total === 1 ? "" : "s"}`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={cx("rounded-full border px-3 py-1 text-xs font-black", meta.pill)}>
        {meta.label}
      </span>

      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-700">
        {label}
      </span>

      {chapterDisplay && (
        <span className="max-w-[220px] truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-black text-slate-500">
          {chapterDisplay}
        </span>
      )}
    </div>
  );
}

function DotStrip({ ratings }) {
  if (!ratings?.length) return null;

  return (
    <div className="mt-3 flex max-w-full flex-wrap items-center gap-1">
      {ratings.map((word, index) => {
        const meta = colorOf(word?.rating || "green");

        return (
          <span
            key={`${word?.index ?? index}-${word?.word || "word"}`}
            title={`${word?.word || "Word"}: ${meta.label}`}
            className={cx("h-2 w-2 rounded-full", meta.dot)}
          />
        );
      })}
    </div>
  );
}


function AnnotatedWord({ word }) {
  const wordRating = word?.rating || "green";
  const wordMeta = colorOf(wordRating);
  const letters = word?.letters || [];
  const hasMarkedLetter = letters.some((letter) => letter?.rating && letter.rating !== "green");
  const wordChanged = wordRating !== "green";

  return (
    <span
      className={cx(
        "inline whitespace-nowrap rounded-md px-0.5 py-0.5 font-semibold",
        wordChanged && !hasMarkedLetter ? cx(wordMeta.bg, wordMeta.text) : "text-slate-900"
      )}
      title={`${word?.word || "Word"}: ${wordMeta.label}`}
    >
      {letters.length > 0
        ? letters.map((letter, index) => {
            const rating = letter?.rating || "green";
            const meta = colorOf(rating);
            const changed = rating !== "green";

            return (
              <span
                key={`${letter?.index ?? index}-${letter?.char || ""}`}
                className={cx(
                  "inline rounded-[4px]",
                  changed
                    ? cx("px-[2px] py-[1px] font-black", meta.mark)
                    : "font-semibold text-slate-900"
                )}
                title={`${letter?.char || ""}: ${meta.label}`}
              >
                {letter?.char}
              </span>
            );
          })
        : word?.word}
    </span>
  );
}

function AnnotatedSentence({ ratings }) {
  if (!ratings?.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm font-bold text-slate-400">
        No detailed rating available.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
      <p className="max-w-5xl text-[17px] font-medium leading-9 tracking-normal text-slate-900">
        {ratings.map((word, index) => (
          <React.Fragment key={`${word?.index ?? index}-${word?.word || "word"}`}>
            <AnnotatedWord word={word} />
            {index < ratings.length - 1 ? " " : ""}
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

function Legend() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold text-slate-500">
      <span className="mr-1 text-slate-400">Legend:</span>

      <span className="rounded-full border border-green-300 bg-green-50 px-2 py-1 text-green-700">
        Good
      </span>

      <span className="rounded-full border border-yellow-300 bg-yellow-50 px-2 py-1 text-yellow-800">
        Almost
      </span>

      <span className="rounded-full border border-red-300 bg-red-50 px-2 py-1 text-red-700">
        Needs work
      </span>
    </div>
  );
}

export default function RateMeReplyBlock({ reply }) {
  const [open, setOpen] = useState(false);

  const sentenceRating = reply?.sentenceRating || "green";
  const sentenceMeta = colorOf(sentenceRating);
  const ratings = reply?.ratings || [];
  const note = getNote(reply);
  const chapterDisplay = getChapterDisplay(reply);

  const changes = useMemo(() => countChanges(reply), [reply]);

  return (
    <div
      className={cx(
        "mt-4 overflow-hidden rounded-[24px] border bg-white shadow-sm",
        sentenceMeta.border
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={cx(
          "flex w-full items-start justify-between gap-4 px-4 py-3 text-left transition hover:bg-slate-50",
          sentenceRating === "green" ? "bg-white" : sentenceMeta.bg
        )}
      >
        <div className="min-w-0 flex-1">
          <SummaryPill
            sentenceRating={sentenceRating}
            changes={changes}
            chapterDisplay={chapterDisplay}
          />

          {!open && <DotStrip ratings={ratings} />}

          {!open && note && (
            <div className="mt-3 line-clamp-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold leading-6 text-slate-700">
              “{note}”
            </div>
          )}
        </div>

        <span className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-black text-slate-600 shadow-sm">
          {open ? "Hide" : "Details"}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-4">
          <AnnotatedSentence ratings={ratings} />
          <Legend />

          {note && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                Note
              </div>

              <p className="mt-1 whitespace-pre-wrap text-sm font-semibold leading-6 text-slate-800">
                {note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}